import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiSearch,
  FiFilter,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiMail,
  FiPhone,
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiCalendar,
  FiRefreshCw,
  FiUser,
  FiFileText,
  FiImage
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { usersAPI } from '../services/api';
import { API_CONFIG } from '../config/api';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';
import ImageUpload from '../components/ImageUpload';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [downloadingDocs, setDownloadingDocs] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchUsers();
  }, []); // Only fetch once on component mount

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Try to fetch from API first
      try {
        const response = await usersAPI.getUsers(currentPage, pageSize, searchTerm);
        
        // Handle different response structures
        const data = response?.data || response || [];
        const totalPages = response?.totalPages || response?.pagination?.totalPages || 1;
        const totalItems = response?.totalItems || response?.pagination?.totalItems || 0;
        
        setUsers(Array.isArray(data) ? data : []);
        setTotalPages(totalPages);
        setTotalItems(totalItems);
      } catch (apiError) {
        console.log('API endpoints not available, using mock data:', apiError);
        
        // Fallback to mock data
        const mockUsers = [
          {
            _id: '1',
            name: 'John Doe',
            email: 'john@example.com',
            phone: '+91 9876543210',
            profilePicture: '',
            aadharNumber: '123456789012',
            aadharUpload: 'uploads/aadhar/john_aadhar.jpg',
            panNumber: 'ABCDE1234G',
            panUpload: 'uploads/pan/john_pan.jpg',
            role: 'user',
            isActive: true,
            createdAt: new Date().toISOString()
          },
          {
            _id: '2',
            name: 'Jane Smith',
            email: 'jane@example.com',
            phone: '+91 9876543211',
            profilePicture: '',
            aadharNumber: '123456789013',
            aadharUpload: '',
            panNumber: 'ABCDE1235H',
            panUpload: 'uploads/pan/jane_pan.pdf',
            role: 'agent',
            isActive: true,
            createdAt: new Date(Date.now() - 86400000).toISOString()
          },
          {
            _id: '3',
            name: 'Admin User',
            email: 'admin@example.com',
            phone: '+91 9876543212',
            profilePicture: '',
            aadharNumber: '',
            aadharUpload: '',
            panNumber: '',
            panUpload: '',
            role: 'admin',
            isActive: true,
            createdAt: new Date(Date.now() - 172800000).toISOString()
          }
        ];
        
        setUsers(mockUsers);
        setTotalPages(1);
        setTotalItems(mockUsers.length);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setUsers([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.deleteUser(userId);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleSave = async (userData) => {
    try {
      if (editingUser) {
        await usersAPI.updateUser(editingUser._id, userData);
        toast.success('User updated successfully');
      } else {
        await usersAPI.createUser(userData);
        toast.success('User created successfully');
      }
      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      
      // Handle validation errors from backend
      if (error.response && error.response.data && error.response.data.errors) {
        const validationErrors = error.response.data.errors;
        
        // Display each validation error
        validationErrors.forEach(err => {
          toast.error(`${err.path}: ${err.msg}`, {
            duration: 4000,
            position: 'top-right',
          });
        });
      } else if (error.response && error.response.data && error.response.data.message) {
        // Handle single error message
        toast.error(error.response.data.message);
      } else {
        // Generic error message
        toast.error('Failed to save user');
      }
    }
  };

  const exportToExcel = () => {
    const filteredData = users.filter(user => {
      const matchesText = user.name?.toLowerCase().includes(filterText.toLowerCase()) ||
                         user.email?.toLowerCase().includes(filterText.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      return matchesText && matchesRole;
    });

    const ws = XLSX.utils.json_to_sheet(filteredData.map(user => ({
      Name: user.name,
      Email: user.email,
      Phone: user.phone,
      Role: user.role,
      Status: user.isActive ? 'Active' : 'Inactive',
      'Created At': new Date(user.createdAt).toLocaleDateString()
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'users.xlsx');
    toast.success('Users exported to Excel');
  };

  const exportToPDF = () => {
    const filteredData = users.filter(user => {
      const matchesText = user.name?.toLowerCase().includes(filterText.toLowerCase()) ||
                         user.email?.toLowerCase().includes(filterText.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      return matchesText && matchesRole;
    });

    const doc = new jsPDF();
    doc.autoTable({
      head: [['Name', 'Email', 'Phone', 'Role', 'Status', 'Created At']],
      body: filteredData.map(user => [
        user.name,
        user.email,
        user.phone,
        user.role,
        user.isActive ? 'Active' : 'Inactive',
        new Date(user.createdAt).toLocaleDateString()
      ]),
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] }
    });
    doc.save('users.pdf');
    toast.success('Users exported to PDF');
  };

  const handleDownloadDocument = async (url, type) => {
    const docKey = `${type}_${url}`;
    setDownloadingDocs(prev => ({ ...prev, [docKey]: true }));
    
    try {
      // Check if URL is a full URL or relative path
      const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.BASE_URL.replace('/api', '')}/${url}`;
      
      const response = await fetch(fullUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const filename = url.split('/').pop() || `${type.toLowerCase()}_document`;
      saveAs(blob, filename);
      toast.success(`${type} document downloaded successfully!`);
    } catch (error) {
      console.error(`Error downloading ${type} document:`, error);
      toast.error(`Failed to download ${type} document. Please check if the file exists.`);
    } finally {
      setDownloadingDocs(prev => ({ ...prev, [docKey]: false }));
    }
  };

  const getDocumentIcon = (url) => {
    if (!url) return null;
    const extension = url.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return <FiImage className="w-3 h-3" />;
    } else if (['pdf'].includes(extension)) {
      return <FiFileText className="w-3 h-3" />;
    } else {
      return <FiDownload className="w-3 h-3" />;
    }
  };

  const columns = [
    {
      name: 'User',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            {row.profilePicture ? (
              <img src={row.profilePicture} alt={row.name} className="w-10 h-10 rounded-full" />
            ) : (
              <FiUser className="w-5 h-5 text-white" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">{row.name}</p>
            <p className="text-sm text-gray-500">{row.email}</p>
          </div>
        </div>
      ),
      width: '250px'
    },
    {
      name: 'Contact',
      selector: row => row.phone,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          <FiPhone className="w-4 h-4 text-gray-400" />
          <span>{row.phone || 'N/A'}</span>
        </div>
      )
    },
    {
      name: 'Documents',
      selector: row => row.aadharNumber,
      sortable: false,
      cell: row => (
        <div className="space-y-2">
          {/* Aadhar Section */}
          <div className="flex items-center space-x-2">
            {row.aadharNumber && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Aadhar: {row.aadharNumber.slice(-4)}
              </span>
            )}
            {row.aadharUpload && (
              <button
                onClick={() => handleDownloadDocument(row.aadharUpload, 'Aadhar')}
                disabled={downloadingDocs[`Aadhar_${row.aadharUpload}`]}
                className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer group ${
                  downloadingDocs[`Aadhar_${row.aadharUpload}`]
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                }`}
                title="Download Aadhar Document"
              >
                {downloadingDocs[`Aadhar_${row.aadharUpload}`] ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                ) : (
                  getDocumentIcon(row.aadharUpload)
                )}
                <span>Aadhar Doc</span>
              </button>
            )}
          </div>
          
          {/* PAN Section */}
          <div className="flex items-center space-x-2">
            {row.panNumber && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                PAN: {row.panNumber}
              </span>
            )}
            {row.panUpload && (
              <button
                onClick={() => handleDownloadDocument(row.panUpload, 'PAN')}
                disabled={downloadingDocs[`PAN_${row.panUpload}`]}
                className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer group ${
                  downloadingDocs[`PAN_${row.panUpload}`]
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                }`}
                title="Download PAN Document"
              >
                {downloadingDocs[`PAN_${row.panUpload}`] ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600"></div>
                ) : (
                  getDocumentIcon(row.panUpload)
                )}
                <span>PAN Doc</span>
              </button>
            )}
          </div>
        </div>
      )
    },
    {
      name: 'Role',
      selector: row => row.role,
      sortable: true,
      cell: row => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.role === 'admin' ? 'bg-red-100 text-red-800' :
          row.role === 'agent' ? 'bg-yellow-100 text-yellow-800' :
          'bg-green-100 text-green-800'
        }`}>
          {row.role}
        </span>
      )
    },
    {
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          {row.isActive ? (
            <FiUserCheck className="w-4 h-4 text-green-500" />
          ) : (
            <FiUserX className="w-4 h-4 text-red-500" />
          )}
          <span className={row.isActive ? 'text-green-600' : 'text-red-600'}>
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      )
    },
    {
      name: 'Created',
      selector: row => row.createdAt,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          <FiCalendar className="w-4 h-4 text-gray-400" />
          <span>{new Date(row.createdAt).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      name: 'Created By',
      selector: row => row.createdBy?.name || 'Admin',
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          <FiUser className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {row.createdBy?.name || 'Admin'}
          </span>
        </div>
      )
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center space-x-2">
          {/* Agent-specific actions - only show for agents */}
          {row.role === 'agent' && (
            <>
              <button
                onClick={() => navigate(`/agent-users/${row._id}`)}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="View Agent Users"
              >
                <FiUsers className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate(`/agent-applications/${row._id}`)}
                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="View Agent Applications"
              >
                <FiFileText className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      ),
      width: row => row.role === 'agent' ? '160px' : '100px'
    }
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: 'transparent'
      }
    },
    headRow: {
      style: {
        backgroundColor: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
        fontWeight: '600'
      }
    },
    rows: {
      style: {
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#f8fafc'
        }
      }
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesText = user.name?.toLowerCase().includes(filterText.toLowerCase()) ||
                       user.email?.toLowerCase().includes(filterText.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesText && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
          <p className="text-gray-600 mt-1">Manage all users in the system</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Role Filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="agent">Agent</option>
              <option value="user">User</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={fetchUsers}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center space-x-2 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>Excel</span>
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center space-x-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          title=""
          columns={columns}
          data={filteredUsers}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 50]}
          sortable
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          }
          noDataComponent={
            <div className="flex flex-col items-center justify-center p-8">
              <FiUsers className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No users found</p>
            </div>
          }
        />
      </div>

      {/* User Modal */}
      <AnimatePresence>
        {showModal && (
          <UserModal
            user={editingUser}
            onSave={handleSave}
            onClose={() => {
              setShowModal(false);
              setEditingUser(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// User Modal Component
const UserModal = ({ user, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    password: '', // Always show for admin
    role: user?.role || 'user',
    isActive: user?.isActive ?? true,
    aadharNumber: user?.aadharNumber || '',
    aadharUpload: user?.aadharUpload || '',
    panNumber: user?.panNumber || '',
    panUpload: user?.panUpload || ''
  });

  const [showPassword, setShowPassword] = useState(!user); // Show password field for new users
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    
    // Remove password from data if it's empty (for updates)
    const submitData = { ...formData };
    if (!submitData.password) {
      delete submitData.password;
    }
    
    try {
      await onSave(submitData);
    } catch (error) {
      // Handle validation errors
      if (error.response && error.response.data && error.response.data.errors) {
        const validationErrors = error.response.data.errors;
        const fieldErrors = {};
        
        validationErrors.forEach(err => {
          fieldErrors[err.path] = err.msg;
        });
        
        setErrors(fieldErrors);
      } else {
        // Re-throw the error to be handled by parent
        throw error;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName];
  };

  const hasFieldError = (fieldName) => {
    return !!errors[fieldName];
  };

  const clearFieldError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    clearFieldError(fieldName);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {user ? 'Edit User' : 'Add New User'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    hasFieldError('name') ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {hasFieldError('name') && (
                  <p className="text-red-500 text-xs mt-1">{getFieldError('name')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    hasFieldError('email') ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {hasFieldError('email') && (
                  <p className="text-red-500 text-xs mt-1">{getFieldError('email')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    hasFieldError('phone') ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {hasFieldError('phone') && (
                  <p className="text-red-500 text-xs mt-1">{getFieldError('phone')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => handleFieldChange('role', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    hasFieldError('role') ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="user">User</option>
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                </select>
                {hasFieldError('role') && (
                  <p className="text-red-500 text-xs mt-1">{getFieldError('role')}</p>
                )}
              </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password {user ? '(leave blank to keep unchanged)' : '*'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('password') ? 'border-red-500' : 'border-gray-300'
                    }`}
                  minLength={user ? 0 : 6}
                  placeholder={user ? 'Leave blank to keep current password' : 'Enter password'}
                  />
                {user && (
                  <p className="text-xs text-gray-500 mt-1">Leave blank to keep the current password.</p>
                )}
                  {hasFieldError('password') && (
                    <p className="text-red-500 text-xs mt-1">{getFieldError('password')}</p>
                  )}
                </div>
            </div>

            {/* Document Information */}
            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Document Information</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                  <input
                    type="text"
                    value={formData.aadharNumber}
                    onChange={(e) => handleFieldChange('aadharNumber', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('aadharNumber') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="12-digit Aadhar number"
                    maxLength={12}
                  />
                  {hasFieldError('aadharNumber') && (
                    <p className="text-red-500 text-xs mt-1">{getFieldError('aadharNumber')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                  <input
                    type="text"
                    value={formData.panNumber}
                    onChange={(e) => handleFieldChange('panNumber', e.target.value.toUpperCase())}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      hasFieldError('panNumber') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                  {hasFieldError('panNumber') && (
                    <p className="text-red-500 text-xs mt-1">{getFieldError('panNumber')}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Document</label>
                  <ImageUpload
                    currentImage={formData.aadharUpload}
                    onImageChange={(value) => handleFieldChange('aadharUpload', value)}
                    label="Upload Aadhar"
                    placeholder="Click to upload Aadhar document"
                    accept="image/*,.pdf"
                    maxSize={10 * 1024 * 1024} // 10MB
                  />
                  {hasFieldError('aadharUpload') && (
                    <p className="text-red-500 text-xs mt-1">{getFieldError('aadharUpload')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN Document</label>
                  <ImageUpload
                    currentImage={formData.panUpload}
                    onImageChange={(value) => handleFieldChange('panUpload', value)}
                    label="Upload PAN"
                    placeholder="Click to upload PAN document"
                    accept="image/*,.pdf"
                    maxSize={10 * 1024 * 1024} // 10MB
                  />
                  {hasFieldError('panUpload') && (
                    <p className="text-red-500 text-xs mt-1">{getFieldError('panUpload')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isSubmitting 
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  user ? 'Update' : 'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Users; 